import { useState, useRef, useCallback, useEffect, useId } from "react";
import {
  Camera,
  CameraResultType,
  CameraSource,
  type Photo,
} from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { getR2PublicUrl } from "@/storage/uploadService";
import {
  createLocalImagePreviewWithFallback,
  revokeObjectUrl,
} from "@/lib/localImagePreview";

const LOCAL_FILE_SCHEME_PATTERN = /^(content|file|capacitor):\/\//i;
const DIRECT_PREVIEW_URL_PATTERN =
  /^(https?:\/\/|blob:|data:|file:\/\/|content:\/\/|capacitor:\/\/)/i;

const IMAGE_EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
};

const IMAGE_MIME_TYPE_BY_FORMAT: Record<string, string> = {
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
};

const normalizePreviewUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) {
    return "";
  }

  if (/^data:/i.test(trimmed)) {
    return trimmed.replaceAll(/\s+/g, "");
  }

  if (LOCAL_FILE_SCHEME_PATTERN.test(trimmed)) {
    try {
      return Capacitor.convertFileSrc(trimmed);
    } catch {
      return trimmed;
    }
  }

  return trimmed;
};

const resolveStoredPreviewUrl = (url: string): string => {
  const normalized = normalizePreviewUrl(url);
  if (!normalized) {
    return "";
  }

  if (DIRECT_PREVIEW_URL_PATTERN.test(normalized)) {
    return normalized;
  }

  if (normalized.startsWith("/assets/")) {
    return normalized;
  }

  return getR2PublicUrl(normalized) || normalized;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to read image preview"));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read image preview"));
    reader.readAsDataURL(file);
  });

const readFileAsArrayBuffer = async (file: File): Promise<ArrayBuffer> => {
  return file.arrayBuffer();
};

const toBase64DataUrl = (buffer: ArrayBuffer, mimeType: string): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCodePoint(...chunk);
  }

  return `data:${mimeType};base64,${btoa(binary)}`;
};

const createPreviewUrl = async (
  file: File,
): Promise<{ previewUrl: string; fallbackPreviewUrl?: string }> => {
  return createLocalImagePreviewWithFallback(file);
};

const isImageOnlySelection = (acceptedTypes: string[]): boolean => {
  return (
    acceptedTypes.length > 0 &&
    acceptedTypes.every((type) => type.startsWith("image/"))
  );
};

const isUserCancelledNativeSelection = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes("cancel") ||
    message.includes("cancelled") ||
    message.includes("canceled") ||
    message.includes("user denied photos app")
  );
};

const resolveNativePhotoFetchUrl = (
  photo: Pick<Photo, "webPath" | "path">,
): string => {
  const candidates = [photo.webPath, photo.path];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const normalized = normalizePreviewUrl(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return "";
};

const inferNativePhotoMimeType = (
  photo: Pick<Photo, "format">,
  blob: Blob,
): string => {
  if (blob.type) {
    return blob.type;
  }

  const normalizedFormat = photo.format?.trim().toLowerCase();
  if (normalizedFormat && IMAGE_MIME_TYPE_BY_FORMAT[normalizedFormat]) {
    return IMAGE_MIME_TYPE_BY_FORMAT[normalizedFormat];
  }

  return "image/jpeg";
};

const inferNativePhotoExtension = (
  mimeType: string,
  photo: Pick<Photo, "format">,
): string => {
  const normalizedMimeType = mimeType.trim().toLowerCase();
  if (IMAGE_EXTENSION_BY_MIME_TYPE[normalizedMimeType]) {
    return IMAGE_EXTENSION_BY_MIME_TYPE[normalizedMimeType];
  }

  const normalizedFormat = photo.format?.trim().toLowerCase();
  if (normalizedFormat) {
    return normalizedFormat;
  }

  return "jpg";
};

const createFileFromNativePhoto = async (
  photo: Photo,
  fileNamePrefix: string,
): Promise<File> => {
  const fetchUrl = resolveNativePhotoFetchUrl(photo);
  if (!fetchUrl) {
    throw new Error("Missing native photo path");
  }

  const response = await fetch(fetchUrl);
  if (!response.ok) {
    throw new Error(`Failed to read native photo: ${response.status}`);
  }

  const blob = await response.blob();
  const mimeType = inferNativePhotoMimeType(photo, blob);
  const extension = inferNativePhotoExtension(mimeType, photo);

  return new File([blob], `${fileNamePrefix}-${Date.now()}.${extension}`, {
    type: mimeType,
    lastModified: Date.now(),
  });
};

export interface UseImageUploadOptions {
  /** Initial URL to display */
  initialUrl?: string;
  /** Max file size in megabytes */
  maxSizeMB?: number;
  /** Array of accepted mime types */
  acceptedTypes?: string[];
  /** Is the component editable? */
  editable?: boolean;
  /** Is an upload currently in progress? */
  isUploading?: boolean;
  /** Callback fired when a file is successfully selected or invalid */
  onChange?: (file: File, previewUrl: string, error?: string) => void;
  /** Callback fired when the image is removed */
  onRemove?: () => void;
  /** Custom async/sync resolver for URLs (e.g. for structured avatar paths) */
  resolveUrl?: (url: string) => string | Promise<string> | null;
}

export function useImageUpload({
  initialUrl = "",
  maxSizeMB = 5,
  acceptedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
  ],
  editable = true,
  isUploading = false,
  onChange,
  onRemove,
  resolveUrl,
}: UseImageUploadOptions) {
  const uploadInputId = useId().replaceAll(":", "");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(initialUrl);
  const [fallbackPreviewUrl, setFallbackPreviewUrl] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [internalError, setInternalError] = useState<string | undefined>();
  // Guard: prevents useEffect from overwriting a locally-selected preview
  const hasLocalPreview = useRef(false);
  const selectedFileRef = useRef<File | null>(null);
  const previewRecoveryAttemptsRef = useRef(0);
  const previousPreviewUrlRef = useRef("");
  const previousFallbackPreviewUrlRef = useRef("");

  useEffect(() => {
    const previousPreviewUrl = previousPreviewUrlRef.current;
    if (previousPreviewUrl && previousPreviewUrl !== previewUrl) {
      revokeObjectUrl(previousPreviewUrl);
    }

    previousPreviewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(previousPreviewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    const previousFallbackPreviewUrl = previousFallbackPreviewUrlRef.current;
    if (
      previousFallbackPreviewUrl &&
      previousFallbackPreviewUrl !== fallbackPreviewUrl
    ) {
      revokeObjectUrl(previousFallbackPreviewUrl);
    }

    previousFallbackPreviewUrlRef.current = fallbackPreviewUrl;
  }, [fallbackPreviewUrl]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(previousFallbackPreviewUrlRef.current);
    };
  }, []);

  // URL Resolution Effect – skipped when user has selected a local file
  useEffect(() => {
    const normalized = initialUrl.trim();
    if (!normalized) {
      return;
    }

    const isLocalLikeUrl =
      normalized.startsWith("blob:") ||
      normalized.startsWith("data:") ||
      LOCAL_FILE_SCHEME_PATTERN.test(normalized);

    if (!isLocalLikeUrl) {
      hasLocalPreview.current = false;
      selectedFileRef.current = null;
      previewRecoveryAttemptsRef.current = 0;
    }
  }, [initialUrl]);

  useEffect(() => {
    // If user picked a file locally, do NOT let this effect overwrite the preview
    if (hasLocalPreview.current) {
      return;
    }

    let isMounted = true;
    const updatePreview = (url: string) => {
      if (isMounted) setPreviewUrl(url);
    };

    async function loadUrl() {
      const url = initialUrl?.trim();
      if (!url || url === "null" || url === "undefined") {
        setFallbackPreviewUrl("");
        return updatePreview("");
      }

      const isDirectUrl =
        DIRECT_PREVIEW_URL_PATTERN.test(url) ||
        LOCAL_FILE_SCHEME_PATTERN.test(url);
      if (isDirectUrl || !resolveUrl) {
        setFallbackPreviewUrl("");
        return updatePreview(resolveStoredPreviewUrl(url));
      }

      try {
        const resolved = await Promise.resolve(resolveUrl(url));
        setFallbackPreviewUrl("");
        updatePreview(resolveStoredPreviewUrl(resolved || ""));
      } catch (error) {
        console.error("[useImageUpload] URL resolution failed:", error);
        setFallbackPreviewUrl("");
        updatePreview("");
      }
    }

    loadUrl();

    return () => {
      isMounted = false;
    };
  }, [initialUrl, resolveUrl]);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!acceptedTypes.includes(file.type)) {
        return `Chỉ hỗ trợ định dạng ${acceptedTypes
          .map((t) => t.replace("image/", "").toUpperCase())
          .join(", ")}`;
      }
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        return `Kích thước file không được vượt quá ${maxSizeMB}MB`;
      }
      return null;
    },
    [maxSizeMB, acceptedTypes],
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      const error = validateFile(file);
      if (error) {
        setInternalError(error);
        onChange?.(file, "", error);
        return;
      }

      setInternalError(undefined);
      const { previewUrl: nextPreviewUrl, fallbackPreviewUrl: nextFallback } =
        await createPreviewUrl(file);
      hasLocalPreview.current = true; // Lock: prevent effect from overwriting
      selectedFileRef.current = file;
      previewRecoveryAttemptsRef.current = 0;
      setPreviewUrl(nextPreviewUrl);
      setFallbackPreviewUrl(nextFallback || "");
      onChange?.(file, nextPreviewUrl);
      setIsOpen(false);
    },
    [validateFile, onChange],
  );

  const handlePreviewError = useCallback(() => {
    if (fallbackPreviewUrl) {
      previousFallbackPreviewUrlRef.current = "";
      setPreviewUrl(fallbackPreviewUrl);
      setFallbackPreviewUrl("");
      return;
    }

    const shouldRecoverFromSelectedFile =
      previewRecoveryAttemptsRef.current < 1 &&
      Capacitor.isNativePlatform() &&
      Capacitor.getPlatform() === "android" &&
      selectedFileRef.current !== null;

    if (shouldRecoverFromSelectedFile) {
      previewRecoveryAttemptsRef.current += 1;

      void (async () => {
        const selectedFile = selectedFileRef.current;
        if (!selectedFile) {
          setPreviewUrl("");
          return;
        }

        try {
          const recoveredDataUrl = await readFileAsDataUrl(selectedFile);
          setPreviewUrl(recoveredDataUrl);
          return;
        } catch {
          // Continue with array buffer fallback
        }

        try {
          const buffer = await readFileAsArrayBuffer(selectedFile);
          const recoveredDataUrl = toBase64DataUrl(
            buffer,
            selectedFile.type || "image/jpeg",
          );
          setPreviewUrl(recoveredDataUrl);
          return;
        } catch {
          setPreviewUrl("");
        }
      })();

      return;
    }

    setPreviewUrl("");
  }, [fallbackPreviewUrl]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void handleFileSelect(file);
      }
      e.target.value = "";
    },
    [handleFileSelect],
  );

  const openNativePhotoPicker = useCallback(
    async (source: CameraSource) => {
      if (!editable || isUploading) {
        return;
      }

      try {
        setInternalError(undefined);

        const photo = await Camera.getPhoto({
          source,
          resultType: CameraResultType.Uri,
          quality: 90,
          allowEditing: false,
          saveToGallery: false,
          correctOrientation: true,
        });

        const fileNamePrefix =
          source === CameraSource.Camera ? "camera-upload" : "gallery-upload";
        const file = await createFileFromNativePhoto(photo, fileNamePrefix);
        await handleFileSelect(file);
      } catch (error) {
        if (isUserCancelledNativeSelection(error)) {
          return;
        }

        const nativeError =
          "Khong the mo camera hoac thu vien anh tren thiet bi nay";
        console.error("[useImageUpload] Native image selection failed:", error);
        setInternalError(nativeError);
      }
    },
    [editable, handleFileSelect, isUploading],
  );

  const openPicker = useCallback(
    (captureMode?: "environment") => {
      if (!editable || isUploading) {
        return;
      }

      const input = fileInputRef.current;
      if (!input) {
        return;
      }

      input.accept = acceptedTypes.join(",");
      input.value = "";

      if (captureMode) {
        input.setAttribute("capture", captureMode);
      } else {
        input.removeAttribute("capture");
      }

      input.click();
    },
    [acceptedTypes, editable, isUploading],
  );

  const openCamera = useCallback(() => {
    if (Capacitor.isNativePlatform() && isImageOnlySelection(acceptedTypes)) {
      void openNativePhotoPicker(CameraSource.Camera);
      return;
    }

    openPicker("environment");
  }, [acceptedTypes, openNativePhotoPicker, openPicker]);

  const openGallery = useCallback(() => {
    if (Capacitor.isNativePlatform() && isImageOnlySelection(acceptedTypes)) {
      void openNativePhotoPicker(CameraSource.Photos);
      return;
    }

    openPicker();
  }, [acceptedTypes, openNativePhotoPicker, openPicker]);

  const handleRemove = useCallback(() => {
    hasLocalPreview.current = false; // Unlock: allow effect to resolve URLs again
    selectedFileRef.current = null;
    previewRecoveryAttemptsRef.current = 0;
    setPreviewUrl("");
    setFallbackPreviewUrl("");
    setInternalError(undefined);
    onRemove?.();
    setIsOpen(false);
  }, [onRemove]);

  return {
    previewUrl,
    setPreviewUrl,
    isOpen,
    setIsOpen,
    internalError,
    setInternalError,
    fileInputRef,
    openCamera,
    openGallery,
    cameraInputId: `${uploadInputId}-camera`,
    galleryInputId: `${uploadInputId}-gallery`,
    acceptedTypes,
    handleFileInputChange,
    handlePreviewError,
    handleRemove,
  };
}
